import { useCallback, useEffect, useRef, useState } from "react";
import instance from "api/globalaxios";

/**
 * Record from the microphone, transcribe on the backend, insert the text.
 *
 * Extracted from the order sheet's handleMicClick (OrderDrafting.js, the
 * axios version): the same states, the same request (POST audio_file +
 * language to /bac/arbitrator/cases/<id>/transcribe/), the same messages.
 * OrderDrafting.js and SingleDispute.js keep their own copies unchanged; they
 * also append the case's upcoming hearings, which is order-specific.
 *
 * @param {object} options
 * @param {string|null} options.url the transcribe endpoint; null disables
 * @param {(editor, data) => void} [options.onTranscript] what to do with the
 *   response; by default the transcript goes in at the caret as plain text
 * @returns {{ state: "idle"|"recording"|"transcribing", error: string,
 *   message: string, toggle: (editor) => void }} toggle starts recording, or
 *   stops and transcribes; message confirms a transcript went in
 */
export default function useDictation({ url, onTranscript }) {
  const stateRef = useRef("idle");
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const aliveRef = useRef(true);
  const [state, setUiState] = useState("idle");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const latest = useRef({});
  latest.current = { url, onTranscript };

  const setState = (next) => {
    stateRef.current = next;
    if (aliveRef.current) setUiState(next);
  };

  // Closing the dialog mid-recording must not leave the microphone on.
  useEffect(
    () => () => {
      aliveRef.current = false;
      if (stateRef.current === "recording" && recorderRef.current) {
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    },
    []
  );

  const toggle = useCallback(async (editor) => {
    if (stateRef.current === "transcribing") return;
    if (stateRef.current === "recording") {
      recorderRef.current.stop();
      return;
    }
    if (!latest.current.url) return;

    setError("");
    setMessage("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setState("transcribing");

        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        const form = new FormData();
        form.append("audio_file", blob, "recording.webm");
        form.append("language", "en");

        try {
          const res = await instance.post(latest.current.url, form);
          if (!aliveRef.current) return;
          if (latest.current.onTranscript) {
            latest.current.onTranscript(editor, res.data);
          } else if (res.data?.transcript) {
            editor
              .chain()
              .focus()
              .insertContent({ type: "text", text: res.data.transcript })
              .run();
          }
          setMessage(
            res.data?.transcript
              ? "Transcript added at the cursor."
              : "No speech was recognised in the recording."
          );
        } catch (err) {
          const data = err?.response?.data;
          if (aliveRef.current) {
            setError(
              err?.response?.status === 400
                ? data?.detail || data?.error || "Invalid request. Please try again."
                : "AI service unavailable, please try again."
            );
          }
        } finally {
          setState("idle");
        }
      };

      recorder.start();
      recorderRef.current = recorder;
      setState("recording");
    } catch (err) {
      setError("Microphone access denied. Please allow microphone permissions.");
    }
  }, []);

  return { state, error, message, toggle };
}
